import React from 'react';
import { Link } from 'react-router-dom';
import styles from './MarketingPage.module.css';
import sectionStyles from '../components/marketing/sections/sections.module.css';
import CTAGroup from '../components/marketing/CTAGroup';
import FinalCTASection from '../components/marketing/sections/FinalCTASection';
import ExpandableCard from '../components/marketing/ExpandableCard';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useReveal from '../hooks/useReveal';
import {
  ListIcon,
  ChartIcon,
  TeamIcon,
  OrgChartIcon,
  TargetIcon,
  CardIcon,
  RefreshIcon,
  EyeOffIcon,
  CheckIcon,
} from '../components/marketing/MarketingIcons';

const LIVE_CAPABILITIES = [
  {
    Icon: ListIcon,
    title: 'Every live contact in one hub',
    summary: 'One searchable place for everyone your team meets.',
    body: 'Every contact captured by your team in one searchable place, deduplicated across people, and displayed with each contact\u2019s current details rather than a snapshot from the day you met.',
  },
  {
    Icon: ChartIcon,
    title: 'Networking performance, compared',
    summary: 'Activity by person and by team, with leaderboards.',
    body: 'Cards shared and contacts captured, and engagement over time, broken down by team and by individual, showing who is building the most and who needs support.',
  },
  {
    Icon: CardIcon,
    title: 'Premium contactless cards, customised to you',
    summary: 'Metal cards tied to each person\u2019s live profile.',
    body: 'Each team member can carry a premium metal contactless card, permanently linked to their live digital profile. Cards are customised for your organisation. Our design team will work up the artwork with you, or produce your own, and we agree the design with you directly when you order.',
  },
  {
    Icon: RefreshIcon,
    title: 'Profiles that never go stale',
    summary: 'When someone changes role or number, the update follows them.',
    body: 'When someone changes role or number, the update reaches every person they have ever shared their card with. No reprinting, no corrections.',
  },
  {
    Icon: EyeOffIcon,
    title: 'Privacy your team will accept',
    summary: 'Administrators see relationships, not diaries.',
    body: 'Administrators see the organisation\u2019s relationships, not private notes or personal locations, and every bulk view of contact data is audit-logged.',
  },
];

const COMING_CAPABILITIES = [
  {
    Icon: TeamIcon,
    title: 'The seat belongs to the role',
    body: 'Accounts are tied to a position rather than a person, so when someone leaves the organisation keeps the relationships they built and whoever takes the role inherits the contact base.',
    status: 'In development',
  },
  {
    Icon: OrgChartIcon,
    title: 'Automated org charting',
    body: 'The chart is generated from your teams, seats and reporting lines rather than maintained by hand, so you can see the shape of the organisation and who holds which relationships.',
    status: 'In development',
  },
  {
    Icon: TargetIcon,
    title: 'Which occasions produced business',
    body: 'Compare event types and specific occasions by what came out of them, so you can put next year\u2019s budget behind the ones that convert instead of the ones that fill the diary.',
    status: 'In development',
  },
];

const OUTCOMES = [
  {
    term: 'Networking activity becomes visible',
    body: 'Most organisations have no idea what their teams produce at events and meetings. Velox turns that into something you can look at, compare between people, and act on.',
  },
  {
    term: 'Relationships stay with the business',
    body: 'When contacts live in a shared hub rather than in individual pockets and phones, the value of a conference or a season of meetings does not walk out with one person.',
  },
  {
    term: 'Spend goes where it works',
    body: 'Judging an event by what it produced rather than by how it felt changes which sponsorships get renewed and which stands are worth staffing.',
  },
  {
    term: 'Consistent professional representation',
    body: 'Every team member presents the same current, accurate profile on a card that carries your branding, rather than a mix of old printed cards and half-finished signatures.',
  },
  {
    term: 'Less administrative clean-up',
    body: 'Contact details maintained by their owners means fewer stale records, and less time spent correcting them by hand.',
  },
];

const AUDIENCES = [
  'Sales teams working conferences and client meetings',
  'Business development and partnerships',
  'Recruitment teams meeting candidates at scale',
  'Events teams who need to show what a stand produced',
];

const EnterprisePage = () => {
  const revealRef = useReveal();

  useDocumentMeta({
    title: 'Velox for teams',
    description:
      'Give your customer-facing team a shared company contact hub that keeps every contact live, analytics comparing performance across teams, and optional customised metal cards.',
    path: '/enterprise',
  });

  return (
    <div ref={revealRef}>
      <section className={styles.pageHero}>
        <div className="vxm-grid-motif" />
        <div className={`vxm-container vxm-container--narrow ${styles.pageHeroInner}`}>
          <p className="vxm-eyebrow">Velox for teams</p>
          <h1 className="vxm-h1">
            Every introduction your team makes, working for the business
          </h1>
          <p className="vxm-lead">
            A shared contact hub for the organisation, the analytics to see
            what all that meeting is actually producing, and contactless cards
            if you want an effortless way to share in the room.
          </p>
          <CTAGroup source="enterprise_hero" intent="team" showSecondary={false} />
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container">
          <div className="vxm-section-head vxm-reveal">
            <h2 className="vxm-h2">What a company account adds</h2>
            <p className="vxm-body">
              Your team gets everything a professional gets. The company account
              adds the layer above it.
            </p>
          </div>
          <div className={sectionStyles.cardGrid}>
            {LIVE_CAPABILITIES.map(({ Icon, title, summary, body }) => (
              <ExpandableCard
                key={title}
                title={title}
                summary={summary}
                icon={<Icon />}
                alwaysOpenOnDesktop
              >
                <p className="vxm-body">{body}</p>
              </ExpandableCard>
            ))}
          </div>

          <div className={sectionStyles.comingBlock}>
            <h3 className={sectionStyles.featureGroupTitle}>Coming for teams</h3>
            <div className={sectionStyles.cardGrid}>
              {COMING_CAPABILITIES.map(({ Icon, title, body, status }) => (
                <ExpandableCard
                  key={title}
                  title={title}
                  status={status}
                  icon={<Icon />}
                  alwaysOpenOnDesktop
                >
                  <p className="vxm-body">{body}</p>
                </ExpandableCard>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="vxm-section">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">Why teams adopt it</h2>
          </div>
          <ul className={styles.detailList} style={{ marginTop: 'var(--vxm-space-6)' }}>
            {OUTCOMES.map((item) => (
              <li key={item.term} className={`${styles.detailRow} vxm-reveal`}>
                <h3 className={styles.detailTerm}>{item.term}</h3>
                <p className={styles.detailBody}>{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="vxm-section vxm-section--raised">
        <div className="vxm-container vxm-container--narrow">
          <div className="vxm-reveal">
            <h2 className="vxm-h2">Who it is for</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-5)' }}>
              Velox is built for teams whose work happens face to face.
            </p>
            <ul className={sectionStyles.featureList}>
              {AUDIENCES.map((audience) => (
                <li key={audience} className={sectionStyles.featureItem}>
                  <CheckIcon
                    className={sectionStyles.featureCheck}
                    width={18}
                    height={18}
                  />
                  <span>{audience}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="vxm-section">
        <div className="vxm-container vxm-container--narrow">
          <div className={`${styles.panel} vxm-reveal`}>
            <h2 className={styles.panelTitle}>Where the line sits</h2>
            <p className="vxm-body" style={{ marginBottom: 'var(--vxm-space-5)' }}>
              A company contact hub only works if the people filling it trust
              it. Administrators see relationships, not private notes or
              personal locations. Consent is captured when someone joins, and
              every bulk view of contact data is recorded. Company accounts are
              set up with you rather than self-serve.
            </p>
            <Link to="/security-privacy" className="vxm-btn vxm-btn--secondary">
              Security and privacy
            </Link>
          </div>
        </div>
      </section>

      <FinalCTASection
        title="Tell us about your team"
        body="Tell us your team size, whether you want cards, and what you want to track. We will come back with the admin view, the contact hub, the analytics and a price."
        source="enterprise_final"
        intent="team"
        showSecondary={false}
      />
    </div>
  );
};

export default EnterprisePage;
